import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import closeicon from "../../../assets/images/close.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import {
  selectSmartDeviceQuote,
  selectSmartDeviceQuoteLoading,
} from "../../../redux/features/quoteSlice";
import { selectSmartDeviceForm } from "../../../redux/features/smartDeviceFormSlice";
import { formatPremiumValue, formatPremiumWithCurrency } from "../../../utils";
import BackdropModal from "../../common/modal/backdropModal";

const SmartDeviceSummaryModal = ({ show, onClose }) => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";

  // Helper function for currency formatting with proper RTL support
  const formatCurrencyForDisplay = (amount) => {
    if (isRTL) {
      // Arabic: use English numerals and reverse order (amount currency)
      return `${formatPremiumValue(amount, "en-US", 3)} د.ب`;
    } else {
      // English: standard format (currency amount)
      return formatPremiumWithCurrency(amount, "BHD", "en-US");
    }
  };

  const smartDeviceFormData = useSelector(selectSmartDeviceForm);
  const smartDeviceQuote = useSelector(selectSmartDeviceQuote);
  const quoteLoading = useSelector(selectSmartDeviceQuoteLoading);

  if (!show) return null;

  // Get data from forms
  const deviceSelectionData =
    smartDeviceFormData["SMART_DEVICE_SELECTION_FORM"] || {};
  const purchaseDetailsData =
    smartDeviceFormData["SMART_DEVICE_PURCHASE_DETAILS_FORM"] || {};
  const contactDetailsData =
    smartDeviceFormData["SMART_DEVICE_CONTACT_DETAILS_FORM"] || {};

  // Use real API data
  const basePremium = smartDeviceQuote?.premium || 0;
  const taxFromAPI = smartDeviceQuote?.tax || 0;
  const totalFromAPI = smartDeviceQuote?.totalPremium || 0;

  // Get real quotation number from API
  const quotationNumber =
    smartDeviceQuote?.quoteNumber || smartDeviceQuote?.policyNumber || "N/A";

  // Get device details
  const deviceType = deviceSelectionData?.deviceType || "N/A";
  const brand = deviceSelectionData?.brand || "N/A";
  const devicePrice = purchaseDetailsData?.devicePrice || "0";

  // Use actual API data
  const finalTax = taxFromAPI;
  const finalTotal = totalFromAPI;

  return (
    <BackdropModal show={show} onClose={onClose} hideClosebtn>
      <>
        {/* Header */}
        <div className="flex justify-between items-center mb-4 -mt-6">
          <h2 className="text-[#1A2024] text-2xl not-italic font-semibold leading-6">
            {translate("Summary", "التسعيرة")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full max767:bg-[#E5E9EB]"
          >
            <img src={closeicon} alt="Close" className="w-6 h-6" />
          </button>
        </div>
        <div className="bg-white rounded-lg max-h-[80vh] min-w-full lg:min-w-[624px] max767:max-w-full max767:min-w-full">
          {/* Summary Details */}
          <div className="rounded-lg summary-content">
            {/* Quotation Number */}
            <div className="flex items-center justify-between bg-[#E8F4D8] px-4 py-2.5 rounded-md mb-2">
              <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
                {translate("Quotation Number", "رقم التسعيرة")}
              </span>
              <span className="text-[#5B6871] text-xs not-italic font-medium leading-6">
                {quotationNumber}
              </span>
            </div>

            {/* Premium */}
            <div className="flex justify-between mb-2 px-0 py-2.5">
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {translate("Premium", "المبلغ")}
              </span>
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {formatCurrencyForDisplay(basePremium)}
              </span>
            </div>

            {/* VAT */}
            <div className="flex justify-between mb-2 px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {translate("VAT", "ضريبة القيمة المضافة")} 10%
              </span>
              <span className="text-[#1A2024] text-sm not-italic font-normal leading-6">
                {formatCurrencyForDisplay(finalTax)}
              </span>
            </div>

            {/* Total */}
            <div className="flex justify-between px-0 py-2.5 border-t-[#DDE2E4] border-t border-solid">
              <span className="text-[#1A2024] text-base not-italic font-extrabold leading-6">
                {translate("Total", "المجموع")}
              </span>
              <span className="text-[#1A2024] text-base not-italic font-extrabold leading-6">
                {formatCurrencyForDisplay(finalTotal)}
              </span>
            </div>
          </div>
        </div>
      </>
    </BackdropModal>
  );
};

export default SmartDeviceSummaryModal;
